import { CommonHelperFunctions, IWorkflowEngineDownloadConfig } from "@kuika/kuika-cl-model";
import Axios, { AxiosResponse } from "axios";
import { KMainFunctions } from "../shared/utility/main-functions";

const backendUrl: string = CommonHelperFunctions.GetBackendUrl();

export interface IWorkflowEngineStatus {
  isRunning: boolean;
  port: number;
  /** Browser-reachable base URL of the engine's admin console; linked in the card while running. */
  adminUrl?: string;
}

export interface IWorkflowEngineDownloadRequest {
  projectId: string;
  selectedConfId: string;
  downloadConfig: IWorkflowEngineDownloadConfig;
}

// Manual lifecycle for the domain's standalone Workflow engine. Status is consumed over SSE (see
// workflow-engine-status-stream.ts), not fetched here. (Download goes through the normal generate flow
// with a config, handled in the screen-design provider.)
export class WorkflowEngineService {
  /**
   * Start is SELF-PROVISIONING when it carries the project id: the backend allocates the engine
   * port and renders the domain's engine appsettings from this project's run config on demand, so
   * starting no longer requires a prior preview to have produced them.
   */
  public static async Start(projectId?: string): Promise<boolean> {
    try {
      await Axios.post<null>(`${backendUrl}/workflowengine/start`, { projectId: projectId ?? null });
      return true;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return false;
    }
  }

  public static async Stop(): Promise<boolean> {
    return WorkflowEngineService.action("stop");
  }

  public static async Restart(): Promise<boolean> {
    return WorkflowEngineService.action("restart");
  }

  private static async action(name: "stop" | "restart"): Promise<boolean> {
    try {
      await Axios.post<null>(`${backendUrl}/workflowengine/${name}`);
      return true;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return false;
    }
  }

  // Dedicated engine download — no project validation, no app code-gen. Returns a browser-fetchable URL.
  public static async DownloadEngine(request: IWorkflowEngineDownloadRequest): Promise<string | null> {
    try {
      const response: AxiosResponse<string> = await Axios.post<string>(
        `${backendUrl}/workflowengine/download`,
        request
      );
      return response.data;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return null;
    }
  }

  // Kuika release version the server (and the engine it publishes) was built with — shown in the
  // download dialog. Locally this is the "1.0.0" placeholder; on deployment it is the Jenkins release.
  public static async GetPlatformVersion(): Promise<string | null> {
    try {
      const response: AxiosResponse<string> = await Axios.get<string>(`${backendUrl}/workflowengine/version`);
      const version: string = (response.data ?? "").trim();
      return version.length > 0 ? version : null;
    } catch {
      return null;
    }
  }
}
